import React, { useState, useEffect } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { Link } from 'react-router-dom'
import { submitCV } from '../services/firebase'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'

// Form stilleri ayrı bir obje olarak tanımlandı
const FORM_STYLES = {
  role: {
    bg: 'bg-[#592C82]/10',
    toggleBg: 'bg-[#592C82]/20',
    activeText: 'text-[#592C82]',
    borderColor: 'border-[#592C82]',
    ringColor: 'focus:ring-[#592C82]',
    hoverText: 'hover:text-[#592C82]',
    title: 'SEND US YOUR CV',
    fileText: 'Drop your CV here to upload or click to browse.',
    subtitle: 'Unlock new career opportunities tailored to your skills. Share your details, and let\'s find your perfect match!'
  },
  talent: {
    bg: 'bg-[#7E57C5]/10',
    toggleBg: 'bg-[#7E57C5]/20',
    activeText: 'text-[#7E57C5]',
    borderColor: 'border-[#7E57C5]',
    ringColor: 'focus:ring-[#7E57C5]',
    hoverText: 'hover:text-[#7E57C5]',
    title: 'SEND US YOUR JOB BRIEF',
    fileText: 'Drop your job brief here to upload or click to browse.',
    subtitle: 'Find the right professionals to strengthen your team. Let\'s connect and discuss your hiring needs.'
  }
}

// Form başlangıç durumu ayrı bir obje olarak tanımlandı
const INITIAL_FORM_STATE = {
  name: '',
  email: '',
  phone: '',
  company: '',
  website: '',
  message: '',
  linkedinUrl: '',
  file: null,
  agreeToPolicy: false,
  agreeToDataDeclaration: false
}

// Form doğrulama fonksiyonu ayrı bir fonksiyon olarak tanımlandı
const validateForm = (formData, formType) => {
  if (!formData.name.trim()) {
    toast.error('Please enter your name')
    return false
  }
  if (!formData.email.trim()) {
    toast.error('Please enter your email')
    return false
  }
  if (!formData.email.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/)) {
    toast.error('Please enter a valid email address')
    return false
  }
  if (!formData.phone) {
    toast.error('Please enter your phone number')
    return false
  }
  
  // Türkiye numarası için özel kontrol
  if (formData.phone.startsWith('+90')) {
    const nationalNumber = formData.phone.replace('+90', '')
    if (nationalNumber.length !== 10) {
      toast.error('Turkish phone numbers must be 10 digits')
      return false
    }
  }

  if (formType === 'talent') {
    if (!formData.company.trim()) {
      toast.error('Please enter your company name')
      return false
    }
    if (!formData.website.trim()) {
      toast.error('Please enter your website')
      return false
    }
  }
  if (!formData.message.trim()) {
    toast.error('Please enter your message')
    return false
  }
  if (!formData.agreeToPolicy) {
    toast.error('Please accept the privacy policy')
    return false
  }
  if (!formData.agreeToDataDeclaration) {
    toast.error('Please accept the protection of personal data declaration')
    return false
  }
  return true
}

export default function CVForm({ container = true, initialFormType = 'role', onSuccess }) {
  // State yönetimi tek bir yerde toplandı
  const [formType, setFormType] = useState(initialFormType)
  const [formData, setFormData] = useState({ ...INITIAL_FORM_STATE, formType: initialFormType })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFormData({ ...formData, file })
      setPreviewUrl(URL.createObjectURL(file))
    }
  }

  const handleFormTypeChange = (type) => {
    setFormType(type)
    // Sadece form tipine özel alanlar sıfırlanıyor
    setFormData({
      ...INITIAL_FORM_STATE,
      formType: type,
      // Ortak alanlar korunuyor
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      linkedinUrl: formData.linkedinUrl
    })
    setPreviewUrl(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm(formData, formType)) {
      return
    }

    setIsSubmitting(true)

    try {
      const result = await submitCV({
        ...formData,
        formType
      })
      
      if (result.success) {
        toast.success(formType === 'role' ? 'Your CV has been sent successfully!' : 'Your job brief has been sent successfully!')
        setFormData({ ...INITIAL_FORM_STATE, formType })
        setPreviewUrl(null)
        
        if (onSuccess) {
          setTimeout(onSuccess, 5000)
        }
      }
    } catch (error) {
      console.error('Error:', error)
      toast.error('Error sending form: ' + error.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const currentStyle = FORM_STYLES[formType]

  // Container kontrolü ayrı bir render fonksiyonuna taşındı
  const renderForm = () => (
    <FormContent
      formType={formType}
      formStyles={FORM_STYLES}
      currentStyle={currentStyle}
      handleFormTypeChange={handleFormTypeChange}
      formData={formData}
      setFormData={setFormData}
      handleFileChange={handleFileChange}
      handleSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      previewUrl={previewUrl}
    />
  )

  return container ? (
    <div className="w-full bg-gray-50 py-8 sm:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {renderForm()}
      </div>
    </div>
  ) : renderForm()
}

function FormContent({
  formType,
  formStyles,
  currentStyle,
  handleFormTypeChange,
  formData,
  setFormData,
  handleFileChange,
  handleSubmit,
  isSubmitting,
  previewUrl
}) {
  const [phoneError, setPhoneError] = useState('');

  const handlePhoneChange = (value, country) => {
    // Eğer value boşsa veya undefined ise
    if (!value) {
      setFormData(prev => ({ ...prev, phone: '' }));
      setPhoneError('Please enter a valid phone number.');
      return;
    }

    // Telefon numarasını formatla
    const formattedPhone = `+${value}`;
    setFormData(prev => ({ ...prev, phone: formattedPhone }));

    // Türkiye için 10 haneli numara kontrolü
    if (country.countryCode === 'tr') {
      const nationalNumber = value.replace(country.dialCode, '');
      if (nationalNumber.length !== 10) {
        setPhoneError('Turkish phone numbers must be 10 digits');
      } else {
        setPhoneError('');
      }
    } else {
      setPhoneError('');
    }
  };

  // Kullanıcının ülkesini otomatik algılama
  const detectUserCountry = () => {
    // Tarayıcı diline göre algılama
    const browserLang = navigator.language || navigator.userLanguage;
    const countryFromLang = browserLang.split('-')[1]?.toLowerCase();

    // IP bazlı algılama için fetch kullanımı
    fetch('https://ipapi.co/json/')
      .then(response => response.json())
      .then(data => {
        const detectedCountry = data.country_code?.toLowerCase() || countryFromLang || 'tr';
        setFormData(prev => ({ ...prev, country: detectedCountry }));
      })
      .catch(() => {
        // Hata durumunda tarayıcı dilini veya varsayılan olarak TR kullan
        setFormData(prev => ({ ...prev, country: countryFromLang || 'tr' }));
      });
  };

  // Component mount olduğunda ülke tespiti yap
  useEffect(() => {
    detectUserCountry();
  }, []);

  return (
    <div className="w-full">
      {/* Ana Başlık ve Alt Başlık */}
      <div className="mx-auto max-w-2xl text-center mb-12">
        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          Let's Connect!
        </h2>
        <p className="mt-2 text-lg leading-8 text-gray-600">
          {currentStyle.subtitle}
        </p>
      </div>

      {/* Form Type Toggle */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex rounded-full bg-white p-1 shadow-sm">
          <button
            className={`px-4 sm:px-6 py-2 rounded-full text-sm font-medium transition-colors ${
              formType === 'role'
                ? formStyles.role.toggleBg + ' ' + formStyles.role.activeText
                : 'text-gray-500 ' + formStyles.role.hoverText
            }`}
            onClick={() => handleFormTypeChange('role')}
          >
            Looking for a role?
          </button>
          <button
            className={`px-4 sm:px-6 py-2 rounded-full text-sm font-medium transition-colors ${
              formType === 'talent'
                ? formStyles.talent.toggleBg + ' ' + formStyles.talent.activeText
                : 'text-gray-500 ' + formStyles.talent.hoverText
            }`}
            onClick={() => handleFormTypeChange('talent')}
          >
            Looking for talent?
          </button>
        </div>
      </div>

      {/* Form */}
      <div className={`${currentStyle.bg} rounded-2xl p-4 sm:p-8 transition-colors duration-300`}>
        <h2 className={`text-2xl sm:text-4xl font-bold ${currentStyle.activeText} mb-6 sm:mb-8 text-center`}>
          {currentStyle.title}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" noValidate>
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            <input
              type="text"
              placeholder="Full Name *"
              className="w-full px-4 py-3 rounded-lg bg-white shadow-sm border-0 placeholder-gray-400 focus:ring-2 focus:ring-[#592C82]"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <input
              type="email"
              placeholder="Email Address *"
              className="w-full px-4 py-3 rounded-lg bg-white shadow-sm border-0 placeholder-gray-400 focus:ring-2 focus:ring-[#592C82]"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <div className="w-full">
              <PhoneInput
                country={'tr'}
                value={formData.phone?.replace('+', '')}
                onChange={handlePhoneChange}
                containerStyle={{ 
                  width: '100%',
                  position: 'relative'
                }}
                inputStyle={{ 
                  width: '100%', 
                  height: '48px',
                  borderRadius: '0.5rem',
                  border: 'none',
                  backgroundColor: 'white',
                  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                  paddingLeft: '52px'
                }}
                buttonStyle={{
                  border: 'none',
                  backgroundColor: 'transparent',
                  borderRadius: '0.5rem 0 0 0.5rem'
                }}
                enableSearch={true}
                disableSearchIcon={false}
                searchPlaceholder="Search country..."
                searchNotFound="No country found"
                preferredCountries={['tr', 'us', 'gb', 'de']}
                placeholder="Phone Number *"
                inputClass="focus:ring-2 focus:ring-[#592C82] focus:border-transparent"
              />
              {phoneError && <p className="text-sm text-red-500 mt-1">{phoneError}</p>}
            </div>
            <input
              type="url"
              placeholder="LinkedIn URL (optional)"
              className="w-full px-4 py-3 rounded-lg bg-white shadow-sm border-0 placeholder-gray-400 focus:ring-2 focus:ring-[#592C82]"
              value={formData.linkedinUrl}
              onChange={(e) => setFormData({ ...formData, linkedinUrl: e.target.value })}
            />
            {formType === 'talent' && (
              <>
                <input
                  type="text"
                  placeholder="Company Name *"
                  className="w-full px-4 py-3 rounded-lg bg-white shadow-sm border-0 placeholder-gray-400 focus:ring-2 focus:ring-[#592C82]"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  required
                />
                <input
                  type="url"
                  placeholder="Company Website *"
                  className="w-full px-4 py-3 rounded-lg bg-white shadow-sm border-0 placeholder-gray-400 focus:ring-2 focus:ring-[#592C82]"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  required
                />
              </>
            )}
            <textarea
              placeholder="Your Message *"
              rows="4"
              className="w-full px-4 py-3 rounded-lg bg-white shadow-sm border-0 placeholder-gray-400 focus:ring-2 focus:ring-[#592C82]"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
            />
            <div className="space-y-4">
              <label className={`block p-4 rounded-lg border-2 border-dashed ${currentStyle.borderColor} cursor-pointer hover:bg-gray-50 transition-colors text-center`}>
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx"
                />
                <span className={`${currentStyle.activeText} font-medium`}>
                  {currentStyle.fileText}
                </span>
                {previewUrl && (
                  <p className="mt-2 text-sm text-gray-500">
                    Selected file: {formData.file?.name}
                  </p>
                )}
              </label>
            </div>
            <div className="space-y-4">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  className={`h-4 w-4 rounded border-gray-300 text-[#592C82] focus:ring-[#592C82]`}
                  checked={formData.agreeToPolicy}
                  onChange={(e) =>
                    setFormData({ ...formData, agreeToPolicy: e.target.checked })
                  }
                  required
                />
                <span className="text-sm text-gray-600">
                  I agree to the{' '}
                  <Link to="/privacy-policy" target="_blank" className="text-[#592C82] hover:text-[#7E57C5]">
                    privacy policy
                  </Link>
                </span>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  className={`h-4 w-4 rounded border-gray-300 text-[#592C82] focus:ring-[#592C82]`}
                  checked={formData.agreeToDataDeclaration}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      agreeToDataDeclaration: e.target.checked,
                    })
                  }
                  required
                />
                <span className="text-sm text-gray-600">
                  I agree to the{' '}
                  <Link
                    to="/personal-data-declaration"
                    target="_blank"
                    className="text-[#592C82] hover:text-[#7E57C5]"
                  >
                    protection of personal data declaration
                  </Link>
                </span>
              </label>
            </div>
          </div>
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-8 py-3 rounded-full bg-[#592C82] text-white font-medium hover:bg-[#7E57C5] transition-colors ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
